import { Command } from 'commander';
import chalk from 'chalk';
import { loadConfig, setDefaultWorkspace, setDefaultOrganization } from '../lib/config.js';
import { OutputFormatter, getJsonFlag } from '../lib/output.js';
import { MutagentError, handleError } from '../lib/errors.js';
import { resetSDKClient } from '../lib/sdk-client.js';

const VALID_CONFIG_KEYS = ['apiKey', 'endpoint', 'format', 'timeout', 'defaultWorkspace', 'defaultOrganization'] as const;

export function createConfigCommand(): Command {
  const config = new Command('config')
    .description('Manage CLI configuration')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent config list
  ${chalk.dim('$')} mutagent config get endpoint
  ${chalk.dim('$')} mutagent config set workspace <workspace-id>
  ${chalk.dim('$')} mutagent config set org <org-id>
    `);

  // List command
  config
    .command('list')
    .description('List all configuration')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent config list
  ${chalk.dim('$')} mutagent config list --json
    `)
    .action(() => {
      const isJson = getJsonFlag(config);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      const cfg = loadConfig();

      // Mask API key for security
      const display = {
        ...cfg,
        apiKey: cfg.apiKey ? `${cfg.apiKey.slice(0, 8)}...` : undefined,
      };

      output.output(display);
    });

  // Get command
  config
    .command('get')
    .description('Get configuration value')
    .argument('<key>', 'Configuration key (apiKey, endpoint, format, timeout, defaultWorkspace, defaultOrganization)')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent config get endpoint
  ${chalk.dim('$')} mutagent config get defaultWorkspace
  ${chalk.dim('$')} mutagent config get apiKey --json

${chalk.dim('Keys: apiKey, endpoint, format, timeout, defaultWorkspace, defaultOrganization')}
    `)
    .action((key: string) => {
      const isJson = getJsonFlag(config);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        const cfg = loadConfig();

        if (!(key in cfg)) {
          throw new MutagentError(
            'INVALID_ARGUMENTS',
            `Unknown config key: "${key}"`,
            `Valid keys: ${VALID_CONFIG_KEYS.join(', ')}\n` +
            'Example: mutagent config get endpoint'
          );
        }

        const value = cfg[key as keyof typeof cfg];
        // Convert value to string safely for display
        const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
        const displayValue = key === 'apiKey' && value ? `${stringValue.slice(0, 8)}...` : stringValue;

        if (isJson) {
          output.output({ [key]: displayValue });
        } else {
          output.info(displayValue);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });

  // Set command (with subcommands)
  const set = new Command('set')
    .description('Set configuration value')
    .action(() => {
      set.help();
    });

  // Set workspace subcommand
  set
    .command('workspace')
    .description('Set default workspace ID')
    .argument('<id>', 'Workspace ID to set as default')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent config set workspace <workspace-id>

${chalk.dim('Persists workspace ID so you don\'t need to pass headers on every request.')}
    `)
    .action((id: string) => {
      const isJson = getJsonFlag(config);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      setDefaultWorkspace(id);
      resetSDKClient();

      if (isJson) {
        output.output({ success: true, defaultWorkspace: id });
      } else {
        output.success(`Default workspace set to ${id}`);
      }
    });

  // Set org subcommand
  set
    .command('org')
    .description('Set default organization ID')
    .argument('<id>', 'Organization ID to set as default')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent config set org <org-id>

${chalk.dim('Persists organization ID for org-scoped API keys.')}
    `)
    .action((id: string) => {
      const isJson = getJsonFlag(config);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      setDefaultOrganization(id);
      resetSDKClient();

      if (isJson) {
        output.output({ success: true, defaultOrganization: id });
      } else {
        output.success(`Default organization set to ${id}`);
      }
    });

  config.addCommand(set);

  return config;
}
